import { useQuery } from '@apollo/client/react/index.js';
import {
  cn,
  GlassAvatar,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardHeader,
  GlassInput,
  GlassTabPanel,
  GlassTabs,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Pencil, Search, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { GET_USERS } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── Types ── */

type AdminRole = 'super_admin' | 'admin' | 'viewer';
type UserStatus = 'active' | 'inactive';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  status: UserStatus;
  lastLogin: string;
}

interface PortalUser {
  id: string;
  name: string;
  username: string;
  group: string;
  account: string;
  status: UserStatus;
  lastSync: string;
}

/* ── GraphQL Response Types ── */

interface UserNode {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  username: string | null;
  groupId: number | null;
  userType: string;
  active: number;
  lastLogin: string | null;
}

interface UsersConnection {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: { node: UserNode }[];
}

interface GetUsersData {
  users: UsersConnection;
}

/* ── Helpers ── */

const ADMIN_ROLES = new Set(['super_admin', 'admin', 'viewer']);

function isAdminRole(role: string): role is AdminRole {
  return ADMIN_ROLES.has(role);
}

const roleLabel: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  viewer: 'Viewer',
};

const roleVariant: Record<AdminRole, 'danger' | 'info' | 'default'> = {
  super_admin: 'danger',
  admin: 'info',
  viewer: 'default',
};

const tabs = [
  { id: 'admin', label: 'Admin Users' },
  { id: 'portal', label: 'Portal Users' },
] as const;

type TabId = (typeof tabs)[number]['id'];

function StatusBadge({ status }: { status: UserStatus }): ReactElement {
  return (
    <GlassBadge variant={status === 'active' ? 'success' : 'default'}>
      {status === 'active' ? 'Active' : 'Inactive'}
    </GlassBadge>
  );
}

/* ── Table header cell ── */

function Th({ children, className }: { children: string; className?: string }): ReactElement {
  return (
    <GlassTableHeaderCell
      className={cn('text-xs font-semibold uppercase tracking-wide text-text-tertiary', className)}
    >
      {children}
    </GlassTableHeaderCell>
  );
}

/* ── Admin Users table ── */

function AdminUsersTable({
  rows,
  onEdit,
}: {
  rows: AdminUser[];
  onEdit?: (id: string) => void;
}): ReactElement {
  if (rows.length === 0) {
    return (
      <div className="px-5 py-12 text-center text-sm text-text-tertiary">
        No admin users match your search.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <GlassTable>
        <GlassTableHead>
          <GlassTableRow>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th>Last Login</Th>
            <Th className="text-right">Actions</Th>
          </GlassTableRow>
        </GlassTableHead>
        <GlassTableBody>
          {rows.map((user) => (
            <GlassTableRow key={user.id}>
              <GlassTableCell>
                <div className="flex items-center gap-3">
                  <GlassAvatar name={user.name} size="sm" />
                  <span className="font-medium text-text">{user.name}</span>
                </div>
              </GlassTableCell>
              <GlassTableCell className="text-text-secondary">{user.email}</GlassTableCell>
              <GlassTableCell>
                <GlassBadge variant={roleVariant[user.role]}>{roleLabel[user.role]}</GlassBadge>
              </GlassTableCell>
              <GlassTableCell>
                <StatusBadge status={user.status} />
              </GlassTableCell>
              <GlassTableCell className="text-text-tertiary">{user.lastLogin}</GlassTableCell>
              <GlassTableCell className="text-right">
                <GlassButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onEdit?.(user.id);
                  }}
                >
                  <Pencil size={13} />
                  Edit
                </GlassButton>
              </GlassTableCell>
            </GlassTableRow>
          ))}
        </GlassTableBody>
      </GlassTable>
    </div>
  );
}

/* ── Portal Users table ── */

function PortalUsersTable({
  rows,
  onEdit,
}: {
  rows: PortalUser[];
  onEdit?: (id: string) => void;
}): ReactElement {
  if (rows.length === 0) {
    return (
      <div className="px-5 py-12 text-center text-sm text-text-tertiary">
        No portal users match your search.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <GlassTable>
        <GlassTableHead>
          <GlassTableRow>
            <Th>Name</Th>
            <Th>Username</Th>
            <Th>Group</Th>
            <Th>Account / Client</Th>
            <Th>Status</Th>
            <Th>Last Sync</Th>
            <Th className="text-right">Actions</Th>
          </GlassTableRow>
        </GlassTableHead>
        <GlassTableBody>
          {rows.map((user) => (
            <GlassTableRow key={user.id}>
              <GlassTableCell>
                <div className="flex items-center gap-3">
                  <GlassAvatar name={user.name} size="sm" />
                  <span className="font-medium text-text">{user.name}</span>
                </div>
              </GlassTableCell>
              <GlassTableCell>
                <code className="px-1.5 py-0.5 rounded bg-bg-muted text-text-secondary text-xs font-mono">
                  {user.username}
                </code>
              </GlassTableCell>
              <GlassTableCell className="text-text-secondary">{user.group}</GlassTableCell>
              <GlassTableCell className="text-text-secondary">{user.account}</GlassTableCell>
              <GlassTableCell>
                <StatusBadge status={user.status} />
              </GlassTableCell>
              <GlassTableCell className="text-text-tertiary">{user.lastSync}</GlassTableCell>
              <GlassTableCell className="text-right">
                <GlassButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onEdit?.(user.id);
                  }}
                >
                  <Pencil size={13} />
                  Edit
                </GlassButton>
              </GlassTableCell>
            </GlassTableRow>
          ))}
        </GlassTableBody>
      </GlassTable>
    </div>
  );
}

/* ── Page ── */

export function UsersPage(): ReactElement {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('admin');
  const [search, setSearch] = useState('');

  const {
    data: rawData,
    loading,
    error,
  } = useQuery(GET_USERS, {
    variables: { first: 100 },
  });

  const allNodes: UserNode[] =
    (rawData as GetUsersData | undefined)?.users.edges.map((e) => e.node) ?? [];

  const adminUsers: AdminUser[] = allNodes
    .filter((node): node is UserNode => isAdminRole(node.userType))
    .map((node) => ({
      id: node.id,
      name:
        [node.firstName, node.lastName].filter((s): s is string => s !== null).join(' ') ||
        (node.username ?? '—'),
      email: node.email ?? '',
      role: node.userType as AdminRole,
      status: node.active === 1 ? 'active' : 'inactive',
      lastLogin: node.lastLogin ?? '—',
    }));

  const portalUsers: PortalUser[] = allNodes
    .filter((node): boolean => !isAdminRole(node.userType))
    .map((node) => ({
      id: node.id,
      name:
        [node.firstName, node.lastName].filter((s): s is string => s !== null).join(' ') ||
        (node.username ?? '—'),
      username: node.username ?? '',
      group: node.groupId !== null ? `Group ${String(node.groupId)}` : '—',
      account: '—',
      status: node.active === 1 ? 'active' : 'inactive',
      lastSync: node.lastLogin ?? '—',
    }));

  const normalizedSearch = search.toLowerCase().trim();

  const filteredAdminUsers = adminUsers.filter(
    (u) =>
      u.name.toLowerCase().includes(normalizedSearch) ||
      u.email.toLowerCase().includes(normalizedSearch) ||
      roleLabel[u.role].toLowerCase().includes(normalizedSearch),
  );

  const filteredPortalUsers = portalUsers.filter(
    (u) =>
      u.name.toLowerCase().includes(normalizedSearch) ||
      u.username.toLowerCase().includes(normalizedSearch) ||
      u.group.toLowerCase().includes(normalizedSearch) ||
      u.account.toLowerCase().includes(normalizedSearch),
  );

  const activeCount =
    activeTab === 'admin' ? filteredAdminUsers.length : filteredPortalUsers.length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Manage admin dashboard access and portal field technician accounts.
        </p>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            void navigate('/users/new');
          }}
        >
          <UserPlus size={15} />
          Invite User
        </GlassButton>
      </div>

      {/* Main card */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Tabs */}
            <GlassTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={(id): void => {
                setActiveTab(id as TabId);
                setSearch('');
              }}
            />

            {/* Search + count */}
            <div className="flex items-center gap-3">
              <GlassBadge variant="default">{loading ? '…' : activeCount} users</GlassBadge>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
                />
                <GlassInput
                  placeholder={
                    activeTab === 'admin' ? 'Search by name or email…' : 'Search by name or group…'
                  }
                  value={search}
                  onChange={(e): void => {
                    setSearch(e.target.value);
                  }}
                  className="pl-8 w-56"
                />
              </div>
            </div>
          </div>
        </GlassCardHeader>

        {/* Tab panels */}
        {loading ? (
          <div className="p-5 space-y-3">
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
          </div>
        ) : error !== undefined ? (
          <div className="py-12 text-center text-sm text-danger">
            Failed to load users: {error.message}
          </div>
        ) : (
          <GlassTabPanel className="pt-0">
            {activeTab === 'admin' && (
              <AdminUsersTable
                rows={filteredAdminUsers}
                onEdit={(id) => {
                  void navigate(`/users/${id}`);
                }}
              />
            )}
            {activeTab === 'portal' && (
              <PortalUsersTable
                rows={filteredPortalUsers}
                onEdit={(id) => {
                  void navigate(`/users/${id}`);
                }}
              />
            )}
          </GlassTabPanel>
        )}
      </GlassCard>
    </div>
  );
}
